/* eslint-disable max-statements */
import { HeadingBlock } from '@design-system/heading-block';
import { graphql, useStaticQuery } from 'gatsby';
import { Tab, Tabs } from '@design-system/tabs';
import {
  CategoriesFields,
  Events,
  isErrorResult,
} from '@phx-husky/auth-service';
import { ContainerWithLoader } from '@phx-husky/common-components';
import {
  addCustomEventListener,
  removeCustomEventListener,
  triggerCustomEvent,
} from '@phx-husky/custom-event';
import { useProfile } from '@phx-husky/use-profile';
import { useTranslations } from '@phx-husky/use-translations';
import { useUserSession } from '@phx-husky/use-user-session';
import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';

import {
  getEventsString,
  isProfileWithEvents,
  removeEvent,
} from '~/components/HCP/EventSubscriptionButton/helpers';

import { ExtendedUserAttributes } from '../HCP/EventSubscriptionButton/models';
import { EditAddressForm } from './addresses-section/edit-address-form';
import { EventSubscriptionSection } from './event-subscription-section';
import {
  EditAddressFormProps,
  ProfileModalPayload,
  ProfileProps,
} from './models';
import * as styles from './profile.module.scss';
import { SecuritySection } from './security-section';
import { UserDetailsSection } from './user-details-section';

export const Profile = ({ onAccountDelete }: ProfileProps) => {
  const { isSignedIn } = useUserSession();
  const {
    getProfileFields,
    getUserProfileEditRights,
    getUserName,
    setUserAttributes,
    validate,
  } = useProfile();
  const [pending, setPending] = useState(true);
  const [profileFields, setProfileFields] = useState<CategoriesFields | null>(
    null
  );
  const [confirmedUser, setConfirmedUser] = useState(true);
  const { translate } = useTranslations();
  const [addressModalData, setAddressModalData] =
    useState<ProfileModalPayload>();

  const modalQuery = useStaticQuery(graphql`
    query modalEventQuery {
      allContentstackModalContent {
        nodes {
          heading
          content
          confirmBtn: confirm_btn {
            ...ButtonFragment
          }
          cancelBtn: cancel_btn {
            ...ButtonFragment
          }
        }
      }
    }
  `);

  const modalContent = modalQuery.allContentstackModalContent.nodes[0];

  const checkIfFieldsDataShouldBeUpdated = ({
    shouldUpdateProfile,
  }: {
    shouldUpdateProfile?: boolean;
  } = {}) => {
    if (shouldUpdateProfile) {
      setPending(true);
    }
  };

  const onAddressModalClose: EditAddressFormProps['onClose'] = ({
    shouldUpdateProfile,
  }) => {
    setAddressModalData(undefined);
    checkIfFieldsDataShouldBeUpdated({ shouldUpdateProfile });
  };

  const openConfirmLdsModal = useCallback(() => {
    const payload = {
      visible: true,
    };
    triggerCustomEvent(Events.onLdsConfirmation, payload);
  }, []);

  const handleEvent = (event: CustomEvent) => {
    if ('shouldUpdateProfile' in event.detail) {
      checkIfFieldsDataShouldBeUpdated({
        shouldUpdateProfile: !!event.detail.shouldUpdateProfile,
      });
    }
  };

  useEffect(() => {
    const fieldDataUpdateEvents: Array<Events> = [
      Events.onLdsConfirmation,
      Events.onTermsChanged,
    ];

    fieldDataUpdateEvents.forEach((event) => {
      addCustomEventListener(event, handleEvent);
    });

    return () => {
      fieldDataUpdateEvents.forEach((event) => {
        removeCustomEventListener(event, handleEvent);
      });
    };
  }, []);

  useEffect(() => {
    const initProfile = async () => {
      const [editRights, fields] = await Promise.all([
        getUserProfileEditRights(),
        getProfileFields(),
      ]);
      setConfirmedUser(editRights.canEditProfile || false);
      setProfileFields(fields);
      setPending(false);
    };

    if (isSignedIn() && pending) {
      initProfile();
    }
  }, [
    getProfileFields,
    getUserProfileEditRights,
    isSignedIn,
    pending,
    profileFields,
  ]);

  const handleEventUpdate = async (eventUid: string) => {
    const data = await getProfileFields();

    if (!isProfileWithEvents(data)) return;

    const currentEventsStr = getEventsString(data);
    const newEventsStr = removeEvent(eventUid, currentEventsStr);

    try {
      const isValid = await validate({ events: newEventsStr });

      if (!isValid) return;

      const payload: ExtendedUserAttributes = {
        events: newEventsStr,
      };

      const res = await setUserAttributes(payload);

      if (isErrorResult(res)) return;
      setPending(true);
    } catch (error) {
      // console.error(error);
    }
  };

  const eventsField = profileFields?.userDetails?.events;

  const eventsArray =
    typeof eventsField?.defaultValue === 'string'
      ? eventsField.defaultValue
          .split(';')
          .map((e) => e.trim())
          .filter(Boolean)
      : [];

  const [tabValue, setTabValue] = useState(0);
  const handleTabChange = (newValue: number) => {
    setTabValue(newValue);
  };

  const tabs = [
    <Tab title={translate('userDetails')} key="user-details-tab">
      {profileFields?.userDetails && (
        <UserDetailsSection
          fields={profileFields.userDetails}
          sectionNameKey="userDetails"
          editButtonDisabled={pending}
          isLdsConfirmed={confirmedUser}
          openConfirmLdsModal={openConfirmLdsModal}
          onFormCloseCallback={checkIfFieldsDataShouldBeUpdated}
          changeEmailFields={{
            email: profileFields.userDetails.email,
            loginPassword: profileFields.security.loginPassword,
          }}
        />
      )}
    </Tab>,

    <Tab title={translate('userEventSubsctiptions')} key="my-events">
      <EventSubscriptionSection
        eventUids={eventsArray}
        onUnsubscribe={handleEventUpdate}
        modalContent={modalContent}
      />
    </Tab>,

    <Tab title={translate('security')} key="security-tab">
      {profileFields?.security && (
        <SecuritySection
          fields={profileFields.security}
          editButtonDisabled={pending}
          onAccountDelete={onAccountDelete}
          onFormCloseCallback={checkIfFieldsDataShouldBeUpdated}
        />
      )}
    </Tab>,
  ].filter(Boolean) as React.ReactElement[];

  return (
    <div className={classNames(styles.wrapper, 'auth-profile__wrapper')}>
      <HeadingBlock
        align="start"
        className={classNames(styles.heading, 'auth-profile__heading')}
        headline={`${translate('profileHeadingPrefix')} ${
          getUserName().userName || ''
        } ${translate('profileHeadingSuffix')}`}
        headlineTag="h2"
        headlineVariant="headline"
        headlineSize="xxxl"
        headlineWeight="regular"
        description={translate('profileHeadingDescription') || ''}
        descriptionSize="xl"
        descriptionVariant="body"
      />
      <ContainerWithLoader
        showLoader={pending && profileFields === null}
        className={classNames(styles.profile, 'auth-profile')}
      >
        <Tabs value={tabValue} onChange={handleTabChange}>
          {tabs}
        </Tabs>
      </ContainerWithLoader>
      {addressModalData?.visible && (
        <EditAddressForm
          fields={addressModalData.fields!}
          title={addressModalData.title!}
          mode={addressModalData.mode}
          additionalAddresses={addressModalData.additionalAddresses}
          onClose={onAddressModalClose}
        />
      )}
    </div>
  );
};
